type DayHours = {
  open: boolean;
  breaks: { from_time: string; to_time: string }[];
  from_time: string;
  to_time: string;
};

type BusinessHours = Record<
  'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday',
  DayHours
>;

const DAY_MAP = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

/**
 * "1000" → "10:00"
 */
export const formatTime = (time?: string): string => {
  if (!time) return '';
  return `${time.slice(0, 2)}:${time.slice(2)}`;
};

/**
 * Default structure
 */
export const getDefaultBusinessHours = (): BusinessHours => {
  return DAY_MAP.reduce((acc, day) => {
    acc[day] = {
      open: false,
      breaks: [],
      from_time: '',
      to_time: ''
    };
    return acc;
  }, {} as BusinessHours);
};

/**
 * Main transformer
 */
export const transformOpeningPeriods = (periods: any = []): BusinessHours => {
  const businessHours = getDefaultBusinessHours();

  // If already in object format (e.g. from state/cookies), return as is
  if (periods && !Array.isArray(periods) && typeof periods === 'object') {
    if (periods.monday || periods.tuesday) {
      return periods as BusinessHours;
    }
  }

  if (!Array.isArray(periods) || !periods.length) return businessHours;

  periods.forEach((period) => {
    const openDay = DAY_MAP[period.open.day];
    const fromTime = formatTime(period.open.time);

    // If close is missing, it usually means open 24 hours
    if (!period.close) {
      businessHours[openDay] = {
        open: true,
        breaks: [],
        from_time: '00:00',
        to_time: '23:59'
      };
      return;
    }

    const closeDay = DAY_MAP[period.close.day];
    const toTime = formatTime(period.close.time);

    // ✅ same-day (your current data case)
    if (openDay === closeDay) {
      businessHours[openDay] = {
        open: true,
        breaks: [],
        from_time: fromTime,
        to_time: toTime
      };
    }
    // ⚠️ overnight support (future-proof)
    else {
      businessHours[openDay] = {
        open: true,
        breaks: [],
        from_time: fromTime,
        to_time: '23:59'
      };

      businessHours[closeDay] = {
        open: true,
        breaks: [],
        from_time: '00:00',
        to_time: toTime
      };
    }
  });

  return businessHours;
};
